import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PhysicalInfoDimension, PhysicalInfoSource, TrackerCustomEvent, useTracker } from 'common-package';
import { Button } from 'antd-mobile';
import service from '@/service';
import { selectCurrentServingOrder, setCurrentServingOrder } from '@/store/service';
import { Header, HeaderMemberInfo, ScrollView, ServiceProcessCrumb } from '@/components';
import { ALL_PAGES } from '@/constant';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import { selectedCurrentCustomer } from '@/store/customer';
import { handleNavFromOrderInfo, Toast } from '@/utils';
import { useNav } from '@/hooks';
import MeasurementContent, { MeasurementContentRefAttrs } from './measurement-content';

import cx from './index.module.scss';

const BodyMeasurement = () => {
  const nav = useNav();
  const dispatch = useDispatch();
  const currentServingOrder = useSelector(selectCurrentServingOrder);
  const currentCustomer = useSelector(selectedCurrentCustomer);
  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
  const [isLoadingLastMeasurement, setIsLoadingLastMeasurement] = useState<boolean>(true);
  const [isHandlingNextStep, setIsHandlingNextStep] = useState<boolean>(false);
  const [measurementResults, setMeasurementResult] = useState<MeasurementResult>(null);
  const measurementContentRef = useRef<MeasurementContentRefAttrs>(null);
  const { sendEvent } = useTracker();

  const handleNextStep = useCallback(async () => {
    try {
      setIsHandlingNextStep(true);

      const { data: order } = await service.service.getServingOrderByOperator();
      if (!order) {
        Toast.show('检测到长时间未操作，已为您自动结束订单');
        nav(ALL_PAGES.MEMBER_DETAIL);
        return;
      }

      await service.bodyMeasurementApi.saveMeasurementResult({
        serviceOrderId: currentServingOrder.orderId,
        type: 1,
        ...measurementResults,
      });

      if (measurementResults !== null) {
        if (Object.keys(measurementResults)?.length > 0 && measurementResults.constructor === Object) {
          // 埋点上报 去掉图片的属性
          // eslint-disable-next-line max-depth
          for (const key in measurementResults) {
            // eslint-disable-next-line max-depth
            if (measurementResults.hasOwnProperty(key) && !key?.includes('Image')) {
              sendEvent(TrackerCustomEvent.FillInPhysicalInfoSource, {
                et: 'CLK',
                c1: PhysicalInfoDimension[key],
                c2: PhysicalInfoSource['physiotherapy-record-before'],
              });
            }
          }
        }
      }

      // 自定义画线直接调用start接口
      if (order?.stepFlow?.find(item => item.step === 'RoboticTask')?.subStep?.includes('CustomPalpation')) {
        try {
          await service.massage.startRoboticTask({ serviceOrderId: order.orderId });
        } catch (e) {
          Toast.fail(e?.message);
        }
      }

      const { data: orderInfo } = await service.service.getServiceOrder(currentServingOrder.orderId);

      dispatch(setCurrentServingOrder(orderInfo));
      await handleNavFromOrderInfo(orderInfo, nav);
    } catch (e) {
      console.warn(e);
    } finally {
      setIsHandlingNextStep(false);
    }
  }, [currentServingOrder.orderId, dispatch, measurementResults, nav, sendEvent]);

  useEffect(() => {
    const { orderId, productItems } = currentServingOrder || {};
    if (!(orderId && productItems?.includes('RadioFrequency'))) return;
    measurementContentRef.current?.getData(orderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentServingOrder?.orderId]);

  return (
    <div className={cx('body-measurement')}>
      <Header
        title={<HeaderMemberInfo name={currentCustomer?.name} avatarUrl={currentCustomer?.avatarUrl} />}
        backConfig={{ text: '会员详情', action: () => nav('/user/member-detail', { replace: true }) }}
      />
      <ServiceProcessCrumb currentPageStep="BodyMeasurement" />
      <ScrollView ref={scrollViewRef} className={cx('content-box')}>
        <div className={cx('content-inner-box')}>
          <MeasurementContent
            ref={measurementContentRef}
            setLoading={setIsLoadingLastMeasurement}
            currentServingOrder={currentServingOrder}
            setMeasurementResult={setMeasurementResult}
          />
        </div>
      </ScrollView>
      <Button
        color="primary"
        className={cx('btn', 'btn-primary', 'next-step', {
          'show-action-point': !(isLoadingLastMeasurement || isHandlingNextStep),
        })}
        loading={isLoadingLastMeasurement || isHandlingNextStep}
        onClick={handleNextStep}
      >
        下一步
      </Button>
    </div>
  );
};

export default BodyMeasurement;
